'use client';

/**
 * AgentCanvasOverlay — concertina-style rich-answer surface.
 *
 * Three detents (closed / half / full) — the overlay grows from the
 * rail's left edge across the canvas pane, never fully obscuring it.
 * The real canvas stays mounted and visibly dimmed underneath the
 * overlay; clicking the dimmed canvas steps the detent down. This
 * makes the overlay read as the rail agent *borrowing* canvas real
 * estate rather than replacing it.
 *
 * Geometry:
 *   - half — w-[52%]; ~half of the canvas pane on the right, the
 *     left half remains visibly dimmed canvas
 *   - full — w-[calc(100%-100px)]; covers most of the canvas pane,
 *     leaves a ~100px sliver of dim canvas on the left so the user
 *     always has a click-to-step-down target and visual reminder
 *     that their work is still there
 *
 * The Canvas component itself stays mounted (no unmount/remount).
 * desktop-shell.tsx applies a brightness filter + pointer-events:none
 * to the Canvas wrapper when this overlay is open, giving the
 * "dimmed but visible" effect across all detents.
 *
 * Dismissal:
 *   - Click visible dimmed canvas → close (user wants the canvas back)
 *   - Esc                          → stepDown (full → half → closed)
 *   - Close button (header)        → close entirely
 *   - Grip on left edge / chevron  → toggle half ↔ full (peer affordance)
 *
 * Canvas-click closes outright (not stepDown) because the gesture says
 * "I want to interact with the canvas, now." Stepping down to half on
 * the first click then forcing a second click to dismiss costs two
 * clicks for what should be one. The grip + Esc keep step-down for
 * users who DO want progressive collapse without dismissal.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useEffect, useState } from 'react';
import { X, Sparkle, FileText, ExternalLink, Maximize2, Minimize2, ArrowRight, Package, DoorOpen } from 'lucide-react';
import { useAgentCanvasStore, type AgentCanvasDetent } from '@/lib/stores/agent-canvas-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { useStrategyData } from '@/lib/api/hooks';
import { useAskComposeStore } from '@/lib/stores/ask-compose-store';
import { CANVAS_CARD_REGISTRY } from '@/components/canvas/cards/card-registry';
import { ObeyaCostPlanRequirementsCard } from '@/components/canvas/cards/obeya/costplan-requirements-card';
import { ObeyaCostPlanComparisonCard } from '@/components/canvas/cards/obeya/costplan-comparison-card';
import { LayoutEngine } from '@/components/canvas/cards/layout-engine';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { CardPlacementProvider } from '@/lib/design/card-export';
import { surfaceBase } from '@/lib/design/tokens';
import { useIntentGrid } from '@/lib/api/hooks';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useAffordance, useAskLabel, useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useRailSurfaceStore } from '@/lib/stores/rail-surface-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import type { CardPlacement, DocsResponse, DocsSource } from '@/lib/api/types';
import type { ComposedAnswer, ArmResult, AskCoordinate } from '@/lib/api/ask-types';
import { cn } from '@/lib/utils/cn';

export function AgentCanvasOverlay() {
  // Affordance addresses — derived from @affordance decorations in
  // klyde_edge/ui_config/composition/affordances/agent_canvas.py and
  // shipped via the matrix wire. Components render the derived
  // test_id; the recording suite targets the same string.
  const frameTestId = useAffordance('overlay.agent_canvas.frame');
  const toggleDetentTestId = useAffordance('overlay.agent_canvas.toggle_detent');
  const gripTestId = useAffordance('overlay.agent_canvas.grip');
  const closeTestId = useAffordance('overlay.agent_canvas.close');
  const detent = useAgentCanvasStore((s) => s.detent);
  const turn = useAgentCanvasStore((s) => s.turn);
  const compose = useAgentCanvasStore((s) => s.compose);
  const linesOverlay = useAgentCanvasStore((s) => s.linesOverlay);
  const costPlanReview = useAgentCanvasStore((s) => s.costPlanReview);
  const close = useAgentCanvasStore((s) => s.close);
  const stepDown = useAgentCanvasStore((s) => s.stepDown);

  // Matrix-driven overlay composition. When `turn === null` (openMatrix()
  // path), the overlay reads `overlay_header_card` + `overlay_body_cards`
  // from the current viewport. When `turn !== null` (openWith() — Ask
  // Klyde legacy), the matrix fields are ignored and the legacy
  // renderer_card + DocsAnswerLayout path renders.
  //
  // Selection chain MUST be useViewportSelection's (explorerSelection >
  // selectedNode.type > tab.type). Raw `activeTab.type` would misroute
  // when a product node is clicked inside a vendor tab — the tab stays
  // 'vendor', content_selection becomes 'product'; raw tab.type would
  // hit mode4_catalog (no overlay) instead of mode4_product (has overlay),
  // emptying the overlay composition and closing immediately.
  const { mode, contentSelection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const viewport = getViewportConfig(mode, contentSelection);
  const overlayHeaderCard = viewport?.overlay_header_card ?? null;
  const overlayBodyCards: CardPlacement[] = viewport?.overlay_body_cards ?? [];

  // Cost Plan review overlay — mode-INDEPENDENT, same shape as
  // `linesOverlay` below: read straight off the store flag, no
  // `getViewportConfig` lookup at all. A trigger living in Composer
  // (composer-lines-card.tsx's "Compare across vendors") needs this to
  // resolve identically no matter which mode is active (mode1, mode3,
  // mode4, mode11 all mount Composer) — a viewport lookup only ever
  // resolved for the two modes (mode3/mode11) that happened to declare
  // a `cost_plan_review` preset, which would've silently no-opped the
  // trigger everywhere else. Those two presets (`mode3_cost_plan_review`
  // / `mode11_cost_plan_review`) carried no canvas/insights footprint of
  // their own and nothing else ever read the `cost_plan_review`
  // selection key, so they were removed as dead weight once this
  // lookup went away. `ObeyaCostPlanComparisonCard` resolves its own
  // mcpId (mcpIdOverride > page selection > pinnedMcpId > editingMcpSid
  // — see that card's docstring), so this needs no per-instance props.
  const isCostPlanReview = costPlanReview;

  const isCompose = compose !== null;
  // A new Ask turn is being computed (submit → next setAnswer). The overlay's
  // `compose` still points at the PREVIOUS turn's answer during this window —
  // its auto-open only re-points once the incoming turn HAS a card, 2-5s later.
  // Painting the prior card the whole time is the "residual card" flash; while a
  // new answer is in flight we warm the compose slot instead (see the render
  // branch). Scoped to isCompose so it never touches matrix/legacy overlays, and
  // it reads `isLoading` — true ONLY between submit and the new turn landing, NOT
  // during the background deep-tier upgrade (upgradeLatestTurn leaves it false),
  // so an in-place tier upgrade of the current answer keeps painting, not skeleton.
  const askComposeLoading = useAskComposeStore((s) => s.isLoading);
  const composeWarming = isCompose && askComposeLoading;
  // Matrix-driven only when neither the legacy turn, a composed answer, nor
  // the room board is set — so those paths don't trip the matrix useEffects
  // below.
  const isMatrixDriven = turn === null && compose === null && !linesOverlay && !costPlanReview;
  // The renderer_card hint that drives data-warming below. Legacy openWith()
  // carries it on turn.response; the compose path (klyde_ask) reads the CLEAN view —
  // the single dispatched card, else the first card of a proposal group. Without this,
  // opening a compose card mounts it empty (the warming only fired for the legacy turn).
  const composeCardHint =
    compose?.view?.card?.renderer_card ??
    compose?.view?.cards?.find((c) => c.renderer_card != null)?.renderer_card ??
    null;
  const rendererCardHint = turn?.response.renderer_card ?? composeCardHint;

  // Warm the data store for renderer_cards that read from their own
  // store rather than from the docs_ask response. The picker only
  // ships `renderer_card` as a hint — the card itself reads from
  // (e.g.) intent-store.gridResponse, which is empty if the user
  // hasn't been to mode3 in this session. Fire the backing strategy
  // when the overlay opens so the card paints with real data.
  //
  // intent_grid with empty params triggers the no-scope fallback in
  // handlers/intent.py, which resolves the calling actor's
  // most-recent active :Intent and its lines.
  const intentGridMutation = useIntentGrid('intent_grid');
  const setGridResponse = useIntentStore((s) => s.setGridResponse);
  const gridResponse = useIntentStore((s) => s.gridResponse);
  useEffect(() => {
    if (detent === 'closed') return;
    if (rendererCardHint !== 'intent_results') return;
    if (gridResponse) return;                       // already populated
    if (intentGridMutation.isPending) return;        // already fetching
    intentGridMutation.mutate(
      { top_k_per_line: 3 },
      { onSuccess: (resp) => setGridResponse(resp) },
    );
  }, [detent, rendererCardHint, gridResponse, intentGridMutation, setGridResponse]);

  // The content_key whose backing data is still warming — the one real async
  // gap in the overlay today: intent_results reads from intent-store, which the
  // effect above is busy populating, so the card would otherwise mount empty for
  // the duration of the fetch. While it's in flight we paint a card skeleton in
  // the mount slot instead (the answer-choreography 'heavy card lands mid-stream'
  // moment). Null once data is present, on error, or for cards whose data rides
  // in the response payload (no warming gap). Per-card knowledge stays here, in
  // the one place that already owns the warming — the mount sites just compare
  // their content_key against it, no per-card branching downstream.
  const warmingCard =
    rendererCardHint === 'intent_results' && !gridResponse && intentGridMutation.isPending
      ? 'intent_results'
      : null;

  // Esc steps the detent down by one (full → half → closed) rather
  // than closing outright — matches the progressive collapse the
  // dimmed-canvas click also drives.
  useEffect(() => {
    if (detent === 'closed') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stepDown();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detent, stepDown]);

  // Auto-close when the matrix-driven viewport has no overlay content
  // for the current (mode, contentSelection). Without this, swapping
  // selection to a viewport with no overlay_header_card + empty
  // overlay_body_cards (e.g. switching vendor while a product PDP is
  // open) makes this component return null at render time, but the
  // store's `detent` stays 'half'/'full'. desktop-shell reads
  // `detent !== 'closed'` to decide whether to keep its canvas
  // brightness-50 + pointer-events-none filter — so the canvas stays
  // visibly dimmed and uninteractive after the overlay has effectively
  // dismissed itself.
  useEffect(() => {
    if (detent === 'closed') return;
    if (!isMatrixDriven) return;
    if (overlayHeaderCard) return;
    if (overlayBodyCards.length > 0) return;
    close();
  }, [detent, isMatrixDriven, overlayHeaderCard, overlayBodyCards.length, close]);

  // Rail-surface swap — kill the mirror. When a matrix-driven overlay
  // opens (PDP, etc.), the right rail's default insights stack would
  // duplicate what the overlay is already showing. Swap it to the
  // Composer (RFQ-builder) for the duration, so the user sees what
  // they're building instead of a smaller copy of what they're
  // inspecting. On close, restore — but only if the rail is still on
  // 'composer', so a manual rail change while the overlay is open
  // (e.g. user clicks chat) isn't clobbered when the overlay dismisses.
  // Doesn't fire for legacy openWith(turn) (Ask Klyde answers) — the
  // rail's existing posture is correct for narrative replies.
  useEffect(() => {
    if (detent === 'closed' || !isMatrixDriven) return;
    const previous = useRailSurfaceStore.getState().active;
    useRailSurfaceStore.getState().setActive('composer');
    return () => {
      const currentNow = useRailSurfaceStore.getState().active;
      if (currentNow === 'composer') {
        useRailSurfaceStore.getState().setActive(previous);
      }
    };
  }, [detent, isMatrixDriven]);

  if (detent === 'closed') return null;
  // Closed when nothing to render — legacy needs a turn; compose needs at
  // least one lane; matrix needs an overlay field populated on the viewport;
  // the lines overlay needs nothing (reads the cart store directly), so
  // it's excluded from the "needs a turn" fallthrough below.
  if (isCompose && compose!.facets.length === 0) return null;
  if (isCostPlanReview) {
    // Nothing else to check — body cards already confirmed non-empty above.
  } else if (!isMatrixDriven && !isCompose && !linesOverlay && !turn) return null;
  else if (isMatrixDriven && !overlayHeaderCard && overlayBodyCards.length === 0) return null;

  // Legacy (Ask Klyde) values — computed only on the legacy branch.
  const response = turn?.response;
  const rendererCard = response?.renderer_card ?? null;
  const Mount = rendererCard ? CANVAS_CARD_REGISTRY[rendererCard] : undefined;
  const HeaderMount = overlayHeaderCard ? CANVAS_CARD_REGISTRY[overlayHeaderCard] : undefined;

  // A product answer renders the SAME mode4_product facet preset as browse/search,
  // NOT the stripped single card. It's resolved by (mode, 'product') — bypassing the
  // search-masked contentSelection that blocks the selection-driven path — so a product
  // is displayed identically no matter which door opened the overlay (Ask / browse /
  // select). The facet cards read the product from viewport.entityId, which the legacy
  // turn path has already set to the answered product. Non-product answers fall through
  // to the legacy renderer + DocsAnswerLayout below.
  const productViewport = getViewportConfig(mode, 'product');
  const productBodyCards: CardPlacement[] = productViewport?.overlay_body_cards ?? [];
  const productHeaderKey = productViewport?.overlay_header_card ?? null;
  const ProductHeaderMount = productHeaderKey ? CANVAS_CARD_REGISTRY[productHeaderKey] : undefined;
  const isProductAnswer = rendererCard === 'product_detail' && productBodyCards.length > 0;

  // Width per detent — anchored to the right edge of the canvas pane,
  // grows leftward. Width transitions are CSS-animated to give the
  // overlay its "concertina" feel.
  // Binary overlay — always full when open (no half). Sliver narrowed
  // from 200px to 130px: still a clearly visible, clickable "your
  // canvas is still there" strip, but hands more width back to content
  // that actually wants it (e.g. the Cost Comparison overlay's vendor
  // columns) rather than leaving it as a fixed, unexamined constant.
  const widthClass = 'w-[calc(100%-100px)]';

  // No half to collapse to, so the grip/chevron just closes the overlay.
  const toggleHalfFull = () => close();

  return (
    <>
      {/* Click-catcher backdrop — invisible (the Canvas's own brightness
          filter, applied in desktop-shell, is the visual dim cue).
          Captures clicks on the visible dimmed canvas and closes the
          overlay outright — the click on the canvas is the user saying
          "I want the canvas now," not "shrink the overlay a bit."
          The grip + Esc keep their progressive-collapse semantics
          (full → half → closed) for users who want to step down rather
          than dismiss. The overlay frame sits above this at z-30 and
          stops propagation so clicks inside the overlay don't dismiss. */}
      <div
        className="absolute inset-0 z-20"
        onClick={close}
        aria-hidden
      />

      {/* Overlay frame — right-anchored, width grows with detent.
          Bordered on the left (the visible edge facing the canvas)
          to make the boundary obvious. */}
      <div
        className={cn(
          'absolute top-0 right-0 bottom-0 z-30',
          'flex flex-col overflow-hidden',
          'border-l-2 border-primary/40 bg-background shadow-2xl',
          'transition-[width] duration-200 ease-out',
          widthClass,
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Agent answer"
        data-testid={frameTestId}
      >
        {/* Grip — clickable strip on the left edge that toggles
            half ↔ full. aria-label is detent-stateful (the screen
            reader announces the destination state). Test addressing
            is via data-testid; aria-label is for real a11y consumers
            only. */}
        <button
          type="button"
          onClick={toggleHalfFull}
          className="absolute -left-1 top-0 bottom-0 z-10 w-2 cursor-col-resize"
          title={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
          aria-label={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
          data-testid={gripTestId}
        >
          <span
            className="absolute left-0.5 top-1/2 h-8 w-[3px] -translate-y-1/2 rounded-sm bg-border"
            aria-hidden
          />
        </button>

        {isMatrixDriven ? (
          <MatrixHeader
            HeaderMount={HeaderMount}
            headerContentKey={overlayHeaderCard}
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        ) : isCompose ? (
          <ComposeHeader
            facetCount={compose!.facets.length}
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        ) : isProductAnswer ? (
          <MatrixHeader
            HeaderMount={ProductHeaderMount}
            headerContentKey={productHeaderKey}
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        ) : linesOverlay ? (
          <LinesOverlayHeader
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        ) : isCostPlanReview ? (
          // No header_card ships on mode1_costplan's own 'mcp' selection
          // either (its canvas card IS the content, no separate header
          // slot) — so this reuses MatrixHeader's chrome-only path
          // (HeaderMount undefined) rather than inventing a bespoke
          // header component, per the file's own cards-everywhere-
          // applied-to-shell-chrome doctrine.
          <MatrixHeader
            HeaderMount={undefined}
            headerContentKey={null}
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        ) : (
          <OverlayHeader
            rendererCard={rendererCard}
            response={response!}
            detent={detent}
            onToggleDetent={toggleHalfFull}
            onClose={close}
            toggleDetentTestId={toggleDetentTestId}
            closeTestId={closeTestId}
          />
        )}

        {/* em base for tokens.ts cards — `text.*` resolve against this;
            gen cards (rem-based text-sm/xs) are unaffected. */}
        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-6" style={{ fontSize: surfaceBase.canvas }}>
          {isMatrixDriven ? (
            // Matrix-driven body — LayoutEngine paints the overlay_body_cards
            // preset on the 12-col grid. Cards self-size; no shell-owned
            // magic-number heights, no per-content_key special-cases.
            // Empty placements render LayoutEngine's "No cards configured"
            // fallback (the early-return above handles the both-empty
            // case, so we only reach here with at least header or body).
            <div className="h-full">
              {/* A full-width column of cards (every card spans all 12 cols —
                  a PDP-style stack, not a dashboard grid) should scroll with
                  each card at its natural height, not compress into one screen
                  height. Grid/multi-column overlays keep the fixed grid. */}
              <LayoutEngine
                placements={overlayBodyCards}
                surface="overlay"
                stack={overlayBodyCards.length > 1 && overlayBodyCards.every((c) => (c.w ?? 1) >= 12)}
              />
            </div>
          ) : isCompose ? (
            // Warm the slot while a NEW Ask turn computes — otherwise the prior
            // turn's card lingers for the 2-5s until the incoming answer lands and
            // re-points `compose`. Skeleton, not the stale card.
            composeWarming ? <CardWarmingSkeleton /> : <ComposeBody compose={compose!} onClose={close} />
          ) : isProductAnswer ? (
            // Product display invariant — the same mode4_product facet preset the
            // selection-driven overlay paints (hero / gallery / specs / description /
            // documents / actions), via the shared LayoutEngine. No stripped-card fork.
            <div className="h-full">
              <LayoutEngine
                placements={productBodyCards}
                surface="overlay"
                stack={productBodyCards.length > 1 && productBodyCards.every((c) => (c.w ?? 1) >= 12)}
              />
            </div>
          ) : linesOverlay === 'rooms' ? (
            <RoomBoardBody />
          ) : isCostPlanReview ? (
            // Mode-independent mount — direct render, same shape as
            // RoomBoardBody above (no LayoutEngine, no viewport lookup).
            // The card reads its own mcpId (mcpIdOverride > page
            // selection > pinnedMcpId > editingMcpSid — see
            // costplan-comparison-card.tsx), so it needs no props here;
            // only a CardPlacementProvider to surface-tag it for scene
            // capture, mirroring the bare renderer_card mount below.
            <div className="h-full">
              <CardPlacementProvider value={{ surface: 'overlay', slot_id: 'obeya_costplan_comparison' }}>
                {/* Compact density: this overlay is narrower than
                    mode1's full canvas tab, and this table's two frozen
                    columns (Requirement, Selected) were eating enough
                    width that barely one vendor column fit before
                    horizontal scroll — defeating the point of a
                    side-by-side comparison. Chrome-only sizing/content
                    knob, no behavior change; mode1's canvas mount below
                    (card-registry.tsx) passes no density prop and keeps
                    today's exact rendering. */}
                <ObeyaCostPlanComparisonCard density="compact" />
              </CardPlacementProvider>
            </div>
          ) : (
            <>
              {/* Stacked layout — mounted renderer_card on top (when
                  shipped), DocsAnswerLayout always below. Reception's
                  web_qa_* preset is what this echoes. Legacy Ask Klyde
                  path; new overlays go through the matrix branch above.
                  The card mount wrapper carries an explicit height because
                  most chart/graph cards (CostTimeToTargetCard,
                  CostForecastBurnCard, agent_graph reagraph fallback, etc.)
                  use `className="h-full"` + recharts `ResponsiveContainer
                  height="100%"` — they collapse to 0×0 when the parent has
                  no defined height. Mode8 gets away with bare <Mount />
                  because its canvas pane parent gives the cards real
                  height through the flex chain; we have to wire that
                  floor ourselves. Detent-responsive: half = 480px (one
                  chart-friendly chunk), full = 720px (room for richer
                  compositions). */}
              {Mount && (
                <section className="rounded-lg border border-border/60 bg-card/30 p-3">
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-primary">
                      {rendererCard}
                    </span>
                    <span>agent picked this card</span>
                  </div>
                  <div className={cn(detent === 'full' ? 'h-[720px]' : 'h-[480px]')}>
                    {/* Surface-tag the bare mount — same duty LayoutEngine's
                        slots perform; without it the card registers into
                        scene.surfaces.unknown instead of 'overlay'. */}
                    <CardPlacementProvider value={{ surface: 'overlay', slot_id: 'renderer_card' }}>
                      {rendererCard === warmingCard ? <CardWarmingSkeleton /> : <Mount />}
                    </CardPlacementProvider>
                  </div>
                </section>
              )}
              <DocsAnswerLayout response={response!} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** Matrix-driven header — paints the overlay_header_card from the
 *  composition (if any) plus the standard chrome (detent chip, half↔full
 *  toggle, close button). No literal title string; no
 *  feature-specific subtitle. When no header_card is shipped, the chrome
 *  alone renders — the body is doing the work, the shell stays out of
 *  the way. Doctrine: cards-everywhere applied to the shell's own
 *  chrome via a slot. */
function MatrixHeader({
  HeaderMount,
  headerContentKey,
  detent,
  onToggleDetent,
  onClose,
  toggleDetentTestId,
  closeTestId,
}: {
  HeaderMount: React.ComponentType<{}> | undefined;
  headerContentKey: string | null;
  detent: AgentCanvasDetent;
  onToggleDetent: () => void;
  onClose: () => void;
  toggleDetentTestId: string | undefined;
  closeTestId: string | undefined;
}) {
  return (
    <div className="flex items-stretch gap-2 border-b border-border bg-card px-3 py-2">
      <div className="flex-1 min-w-0">
        {HeaderMount ? (
          // Offer the header card its declared bundle (rail_card for
          // product_summary_strip) — it renders bare, not via LayoutEngine,
          // so without this it'd fall back to canvas_full.
          <CardChromeProvider contentKey={headerContentKey ?? ''}>
            {/* Surface-tag the bare header mount for scene capture. */}
            <CardPlacementProvider value={{ surface: 'overlay', slot_id: 'overlay_header' }}>
              <HeaderMount />
            </CardPlacementProvider>
          </CardChromeProvider>
        ) : null}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-mono text-primary">
          {detent}
        </span>
        <button
          onClick={onToggleDetent}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
          aria-label={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
          data-testid={toggleDetentTestId}
        >
          {detent === 'full' ? (
            <Minimize2 className="h-3.5 w-3.5" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          onClick={onClose}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Close (Esc steps down)"
          aria-label="Close overlay"
          data-testid={closeTestId}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function OverlayHeader({
  rendererCard,
  response,
  detent,
  onToggleDetent,
  onClose,
  toggleDetentTestId,
  closeTestId,
}: {
  rendererCard: string | null | undefined;
  response: DocsResponse;
  detent: AgentCanvasDetent;
  onToggleDetent: () => void;
  onClose: () => void;
  toggleDetentTestId: string | undefined;
  closeTestId: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Sparkle className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">Ask Klyde · answer</div>
        <div className="text-[10px] text-muted-foreground leading-tight">
          {rendererCard ? (
            <span className="font-mono">{rendererCard}</span>
          ) : (
            <>
              docs-grounded
              {response.confidence && response.confidence !== 'none' && (
                <span> · confidence: {response.confidence}</span>
              )}
              {(response.sources?.length ?? 0) > 0 && (
                <span>
                  {' · '}
                  {response.sources.length} source
                  {response.sources.length === 1 ? '' : 's'}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {/* Detent chip — shows which size we're currently at. */}
      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-mono text-primary">
        {detent}
      </span>
      {/* Half ↔ full toggle (peer affordance to the left-edge grip).
          aria-label is detent-stateful; tests target by data-testid. */}
      <button
        onClick={onToggleDetent}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        aria-label={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        data-testid={toggleDetentTestId}
      >
        {detent === 'full' ? (
          <Minimize2 className="h-3.5 w-3.5" />
        ) : (
          <Maximize2 className="h-3.5 w-3.5" />
        )}
      </button>
      <button
        onClick={onClose}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Close (Esc steps down)"
        aria-label="Close overlay"
        data-testid={closeTestId}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Lines overlay header — room allocation only now. Used to be one
 *  entry point with two tabs (Table / Rooms); the Table tab (qty/
 *  price/spec/remove, bulk-flat-table) is retired — that job now lives
 *  directly on the canvas pivot table's Line-mode rows, committing
 *  immediately instead of needing this overlay opened at all (see
 *  klyde_ipe/docs/mcp-mock-e2e/HANDOVER.md). Room allocation stays here
 *  — a line's quantity split across several room-programme slots is a
 *  genuinely different, wider shape a flat table row can't express, so
 *  it keeps its own layout rather than folding into canvas. No
 *  HeaderMount slot — cart-scoped, not matrix-composed. */
function LinesOverlayHeader({
  detent,
  onToggleDetent,
  onClose,
  toggleDetentTestId,
  closeTestId,
}: {
  detent: AgentCanvasDetent;
  onToggleDetent: () => void;
  onClose: () => void;
  toggleDetentTestId: string | undefined;
  closeTestId: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <DoorOpen className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">Assign rooms</div>
        <div className="text-[10px] text-muted-foreground leading-tight">
          Select a line, then click a room to assign it
        </div>
      </div>
      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-mono text-primary">
        {detent}
      </span>
      <button
        onClick={onToggleDetent}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        aria-label={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        data-testid={toggleDetentTestId}
      >
        {detent === 'full' ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={onClose}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Close (Esc steps down)"
        aria-label="Close overlay"
        data-testid={closeTestId}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface RoomSlot {
  sid: string;
  name: string;
  count: number;
  // Unit types decompose into sub-areas (Living, Bedroom, Study, Balcony,
  // ...) — several RoomSlots can share `name` with a different sub_area,
  // each a distinct furniture-placement target. Shown alongside `name`
  // so those rows don't read as the same row repeated.
  sub_area_label?: string | null;
}
interface RoomProgrammeData {
  room_types: RoomSlot[];
  specific_rooms: RoomSlot[];
}

/** Room board body — cart lines on the left, a per-line qty-per-room
 *  table on the right. Select a line, then type a quantity against
 *  each room it's placed in — a line can span 0, 1, or many rooms
 *  (e.g. 100 bedheads split 40/40/20 across three room types), which a
 *  single click-to-assign control can't express. Blank/0 clears that
 *  room's placement. v1 scope: room-type rows only (no specific-room
 *  drill-in — that needs a real schema decision, not built).
 *
 *  ALLOCATION, not a requirements takeoff: the number typed per room is
 *  the total of the line's ALREADY-ORDERED units going there — a
 *  partition, not a per-single-room figure to be multiplied by the
 *  room's count. A line's placements are meant to sum to <= its
 *  ordered quantity, which is why this body shows a live running total
 *  against it — the room's `×N rooms` badge is informational context
 *  only (e.g. "12 across 6 rooms"), never applied as a multiplier.
 *  Deriving how much SHOULD be ordered from the room programme is a
 *  different, not-yet-built forward-planning question, deliberately
 *  kept out of this control. See RoomPlacement's docstring in
 *  gateway_params.py.
 *
 *  Room data comes from the `mcp_room_programme` BFF strategy —
 *  server-composed, slot_sid is the only identity read from it. Cart
 *  lines are client state (an unsaved cart has no CostPlan for the BFF
 *  to read yet); reading a line's own room_placements array back out
 *  for the input values and summing it for the running total is a
 *  plain lookup/reduce, not new business logic. */
function RoomBoardBody() {
  // Shared with the Table tab (agent-canvas-store) — selecting a line
  // here or clicking its row on the Table tab keeps the same line
  // selected across both, instead of each tab holding its own.
  const selectedLineId = useAgentCanvasStore((s) => s.selectedLineId);
  const setSelectedLineId = useAgentCanvasStore((s) => s.setSelectedLineId);
  const items = useCartStore((s) => s.items);
  const projectId = useCartStore((s) => s.projectId);
  const setItemRoomPlacement = useCartStore((s) => s.setItemRoomPlacement);
  const editingMcpSid = useCartStore((s) => s.editingMcpSid);
  const setItemSourceLineSid = useCartStore((s) => s.setItemSourceLineSid);

  const { data } = useStrategyData<RoomProgrammeData>(
    'mcp_room_programme',
    { project_sid: projectId ?? '' },
    !!projectId,
  );
  const roomTypes = data?.data?.room_types ?? [];
  const specificRooms = data?.data?.specific_rooms ?? [];
  const selectedLine = items.find((i) => i.line_id === selectedLineId) ?? null;
  const allocated = selectedLine
    ? selectedLine.room_placements.reduce((sum, p) => sum + p.qty, 0)
    : 0;
  const overAllocated = !!selectedLine && allocated > selectedLine.quantity;
  // Gate: an unassigned line (no source_line_sid) in a cart that's
  // targeting an existing plan (editingMcpSid set, either via the heavy
  // editMcp reload or the lightweight targetMcpForAppend pick) needs a
  // requirement picked before room allocation makes sense — there's
  // nothing to compare the line against yet. A brand-new plan has
  // nothing to pick against either, so it skips straight to room
  // allocation (out of scope per the room_board spec).
  const needsRequirementPick = !!selectedLine && !selectedLine.source_line_sid && !!editingMcpSid;

  const slotRow = (room: RoomSlot, idx: number, showCount: boolean) => {
    if (!selectedLine) return null;
    const placement = selectedLine.room_placements.find((p) => p.slot_sid === room.sid);
    return (
      <div
        key={room.sid}
        className={cn('flex items-center gap-3 px-3 py-2', idx > 0 && 'border-t border-border/60')}
      >
        <span className="text-sm flex-1 truncate">
          {room.name}
          {room.sub_area_label && (
            <span className="text-muted-foreground"> — {room.sub_area_label}</span>
          )}
        </span>
        {showCount && (
          <span className="text-[10px] font-bold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">
            ×{room.count} rooms
          </span>
        )}
        <input
          type="number"
          min="0"
          value={placement?.qty ?? ''}
          placeholder="0"
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            setItemRoomPlacement(selectedLine.line_id, room.sid, Number.isFinite(val) ? val : 0);
          }}
          className="w-16 h-7 text-center text-sm font-medium bg-background border border-border/60 rounded focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
    );
  };

  return (
    <div className="h-full flex gap-4 -m-4">
      {/* Left: lines — click to select */}
      <div className="w-[300px] flex-shrink-0 border-r border-border overflow-auto p-3 space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-1">
          Lines
        </p>
        {items.map((item) => {
          const selected = item.line_id === selectedLineId;
          const n = item.room_placements.length;
          const placed = item.room_placements.reduce((sum, p) => sum + p.qty, 0);
          const over = placed > item.quantity;
          const unassigned = !item.source_line_sid && !!editingMcpSid;
          return (
            <button
              key={item.line_id}
              onClick={() => setSelectedLineId(selected ? null : item.line_id)}
              className={cn(
                'w-full text-left flex items-center gap-2 rounded-md border p-2 transition-colors',
                selected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-accent',
              )}
            >
              {item.image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={item.image_url} alt={item.name} className="h-8 w-8 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded bg-muted flex-shrink-0">
                  <Package className="h-3.5 w-3.5 text-muted-foreground/60" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-medium truncate">{item.name}</p>
                  {unassigned && (
                    // Same rounded-chip badge pattern as the room-count
                    // badge below (×N rooms), amber instead of primary to
                    // read as a pending state, not informational context.
                    <span className="text-[10px] font-bold text-amber-600 bg-amber-500/10 rounded-full px-1.5 py-0.5 shrink-0">
                      Unassigned
                    </span>
                  )}
                </div>
                <p className={cn('text-[10px] truncate', over ? 'text-red-500 font-medium' : 'text-muted-foreground')}>
                  {n ? `${n} room${n > 1 ? 's' : ''} · ${placed}/${item.quantity} placed` : 'Not assigned'}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Right: requirement pick (unassigned line, existing plan) first,
          falling through to room-type rows once source_line_sid is set. */}
      <div className="flex-1 overflow-auto p-3">
        {!selectedLine ? (
          <p className="text-xs text-muted-foreground">
            Select a line on the left, then enter a quantity against each room it's placed in.
          </p>
        ) : needsRequirementPick ? (
          <div className="h-full flex flex-col">
            <div className="text-xs mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-amber-700">
              <span className="font-medium">{selectedLine.name}</span>
              {' — pick which requirement this is for before allocating rooms.'}
            </div>
            <div className="flex-1 min-h-0 rounded-lg border border-border overflow-hidden">
              <ObeyaCostPlanRequirementsCard
                mcpIdOverride={editingMcpSid}
                onPick={(sourceLineSid, label) =>
                  setItemSourceLineSid(selectedLine.line_id, sourceLineSid, label)}
              />
            </div>
          </div>
        ) : (
          <>
            <div
              className={cn(
                'text-xs mb-3 rounded-md border px-2.5 py-1.5',
                overAllocated ? 'border-red-500/40 bg-red-500/10 text-red-600' : 'border-border/60 bg-muted/30 text-muted-foreground',
              )}
            >
              <span className="font-medium text-foreground">{selectedLine.name}</span>
              {' — '}
              <span className={overAllocated ? 'font-semibold' : 'font-medium tabular-nums'}>
                {allocated} of {selectedLine.quantity} ordered placed
              </span>
              {overAllocated && ' — over-allocated, placements exceed what was ordered'}
            </div>
            {roomTypes.length > 0 && (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-1">
                  Room types
                </p>
                <div className="rounded-lg border border-border overflow-hidden mb-4">
                  {roomTypes.map((room, idx) => slotRow(room, idx, true))}
                </div>
              </>
            )}
            {specificRooms.length > 0 && (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-1">
                  Specific rooms
                </p>
                <div className="rounded-lg border border-border overflow-hidden">
                  {specificRooms.map((room, idx) => slotRow(room, idx, false))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}


/** Structured rendering of a docs_ask response — long_answer + sources
 *  + grounded docs + followups. Always rendered below any mounted
 *  renderer_card so the docs text is never hidden by an empty card. */
function DocsAnswerLayout({ response }: { response: DocsResponse }) {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="prose-sm">
        <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
          {response.long_answer || response.short_answer || '(empty answer)'}
        </div>
      </div>

      {response.sources && response.sources.length > 0 && (
        <SourcesBlock sources={response.sources} />
      )}

      {response.grounded_docs && response.grounded_docs.length > 0 && (
        <GroundedDocsBlock docs={response.grounded_docs} />
      )}

      {response.followup_suggestions && response.followup_suggestions.length > 0 && (
        <FollowupsBlock followups={response.followup_suggestions} />
      )}
    </div>
  );
}

function SourcesBlock({ sources }: { sources: DocsSource[] }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Sources
      </div>
      <ul className="space-y-1.5">
        {sources.map((src) => (
          <li
            key={src.semantic_id}
            className="flex items-start gap-2 rounded border border-border bg-card/50 px-2 py-1.5 text-xs"
          >
            <FileText className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="font-medium leading-tight">{src.title}</div>
              <div className="text-[10px] text-muted-foreground leading-tight">
                {src.package} · {src.section}
                {typeof src.score === 'number' && (
                  <span className="ml-1.5">· score {src.score.toFixed(2)}</span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GroundedDocsBlock({ docs }: { docs: string[] }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Grounded on
      </div>
      <div className="flex flex-wrap gap-1.5">
        {docs.map((d) => (
          <span
            key={d}
            className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
          >
            <ExternalLink className="h-2.5 w-2.5" />
            {d}
          </span>
        ))}
      </div>
    </div>
  );
}

function FollowupsBlock({ followups }: { followups: string[] }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Follow-ups
      </div>
      <ul className="space-y-1">
        {followups.map((f, i) => (
          <li key={i} className="text-xs text-muted-foreground">
            · {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Compose path (klyde_ask compositor) — mount all lanes ─────────────
// The overlay's third mode: a ComposedAnswer's grouped lanes, each mounted as
// its artifact — knowledge's renderer_card + prose, capability's result,
// navigate as a "go" button. The compositor produced the artifacts; this
// surface (the consumer) mounts / fires them. Reuses CANVAS_CARD_REGISTRY,
// same as the legacy renderer_card mount.

/** Card-warming placeholder — fills the mount slot while a renderer_card's
 *  backing data is still loading (see `warmingCard` in the overlay). Fills its
 *  parent (the detent-sized h-[480px]/h-[720px] wrapper owns the height) and
 *  fades to the real card with no reflow when data lands. Generic by design:
 *  it does NOT hint the card kind (chart vs table) — kind-shaped skeletons
 *  read as broken when the guess is wrong, and the sized block already kills
 *  the layout jump. `bg-muted`, reduced-motion-safe, degrades to static bars. */
function CardWarmingSkeleton() {
  return (
    <div
      className="h-full w-full animate-pulse motion-reduce:animate-none rounded-lg border border-border/60 bg-card/20 p-4"
      role="status"
      aria-label="Loading card"
    >
      <div className="mb-3 h-3 w-1/3 rounded bg-muted" />
      <div className="mb-6 h-2 w-1/2 rounded bg-muted" />
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 rounded bg-muted" />
        ))}
      </div>
      <span className="sr-only">Loading card…</span>
    </div>
  );
}

const laneStr = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

function ComposeBody({
  compose,
  onClose,
}: {
  compose: ComposedAnswer;
  onClose: () => void;
}) {
  // Layout = PRESET, not React. The flip's arrangement is the
  // `ask_compose` preset (klyde_edge composition/modes/ask.py) —
  // injected into every mode's selections by total_matrix, resolved
  // here with the SAME getViewportConfig lookup the product overlay
  // uses. The three slot cards (ask_quick_answer / ask_narration /
  // ask_renderer_slot) read the compose store themselves; this
  // component places nothing. Editing the flip's layout is a
  // PlacementSpec edit backend-side — zero React.
  const { mode } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const askViewport = getViewportConfig(mode, 'ask_compose');
  const placements: CardPlacement[] = askViewport?.overlay_body_cards ?? [];
  const navFacets = compose.facets.filter((f) => f.lane === 'navigate');

  return (
    // Inherits the overlay body's canvas base (14px) — per tokens.ts:
    // rail body 13, canvas/overlay body 14, same text.body token.
    <div className="flex h-full flex-col gap-4">
      {placements.length > 0 && (
        <div className="min-h-0 flex-1">
          <LayoutEngine placements={placements} stack surface="overlay" />
        </div>
      )}
      {navFacets.map((facet, i) => (
        <ComposeNavigate key={`nav-${i}`} facet={facet} onClose={onClose} />
      ))}
    </div>
  );
}

function ComposeNavigate({ facet, onClose }: { facet: ArmResult; onClose: () => void }) {
  const openTab = useTabStore((s) => s.openTab);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const coord = facet.payload.coordinate as AskCoordinate | undefined;
  const hint = laneStr(facet.payload.entity_hint) ?? coord?.entity_sid ?? '';
  const etype = laneStr(facet.payload.entity_type) ?? 'entity';
  const navigateLabel = useAskLabel('lane_navigate');
  const goToLabel = useAskLabel('go_to', { name: hint });
  if (!coord?.entity_sid) return null;

  const go = () => {
    const sel = coord.selection || etype;
    // Full context switch: openTab (mode-promote + tab the canvas renders),
    // setExplorerSelection (force the canvas's strategySelection past the home
    // value — explorerSelection wins over tab.type), selectNode (rail).
    openTab({ id: `${sel}-${coord.entity_sid}`, type: sel, title: hint, entityId: coord.entity_sid });
    setExplorerSelection(sel);
    // 2nd arg = the render-context project (the canvas's project cards need it).
    selectNode({ id: coord.entity_sid, label: hint, type: etype }, coord.project_sid);
    onClose();
  };

  return (
    <section className="rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {navigateLabel}
      </div>
      <button
        onClick={go}
        className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm text-primary hover:bg-primary/20 transition-colors"
        title={goToLabel}
      >
        <ArrowRight className="h-4 w-4" />
        {goToLabel}
      </button>
    </section>
  );
}

function ComposeHeader({
  facetCount,
  detent,
  onToggleDetent,
  onClose,
  toggleDetentTestId,
  closeTestId,
}: {
  facetCount: number;
  detent: AgentCanvasDetent;
  onToggleDetent: () => void;
  onClose: () => void;
  toggleDetentTestId: string | undefined;
  closeTestId: string | undefined;
}) {
  const overlayTitle = useAskLabel('overlay_title');
  const lanesLine = useAskLabel(facetCount === 1 ? 'overlay_lanes_one' : 'overlay_lanes_many', { n: facetCount });
  return (
    <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Sparkle className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">{overlayTitle}</div>
        <div className="text-[10px] text-muted-foreground leading-tight">
          {lanesLine}
        </div>
      </div>
      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-mono text-primary">{detent}</span>
      <button
        onClick={onToggleDetent}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        aria-label={detent === 'full' ? 'Collapse to half' : 'Expand to full'}
        data-testid={toggleDetentTestId}
      >
        {detent === 'full' ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={onClose}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Close (Esc steps down)"
        aria-label="Close overlay"
        data-testid={closeTestId}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
